import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { SubmissionButton } from "@thalamos/frontend";
import type {
  IncidentWorkItemWithContext,
  TranslationKey,
  TranslationKeyAndOptions,
} from "@thalamos/vision-common";
import { buildTranslationInput, tk } from "@thalamos/vision-common";

import { Translation } from "../../Translation/Translation.js";
import type { ButtonAction } from "../IncidentTable/types.js";
import { extractIncidentDisplayValues } from "./extractIncidentDisplayValues.js";

export type IncidentWorkItemCardProps = {
  incidentWorkItem: IncidentWorkItemWithContext;
  showAssignedOfficerOnMobile: boolean;
  isPendingTransfer?: boolean;
  primaryButton: ButtonAction;
  secondaryButton?: ButtonAction;
};

export function HeadingAndSubheading({
  heading,
  subheading,
}: {
  heading: TranslationKey | TranslationKeyAndOptions;
  subheading: string;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box>
        <Typography sx={{ fontWeight: "bold" }}>
          <Translation {...buildTranslationInput(heading)} />
        </Typography>
      </Box>
      <Box>
        <Typography>{subheading}</Typography>
      </Box>
    </Box>
  );
}

const PendingTransferBox = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "auto",
        maxWidth: "80%",
        margin: "0 auto",
        marginBottom: "1rem",
        backgroundColor: theme.palette.common.lightBlue,
        borderRadius: "8px",
        padding: "0.5rem 1rem",
      }}
    >
      <Typography
        variant="body1"
        color={theme.palette.common.white}
        px={5}
        sx={{ fontWeight: "bold" }}
      >
        {t("incidentWorkItem.pendingTransfer")}
      </Typography>
    </Box>
  );
};

type DesktopIncidentWorkItemCardProps = {
  typeOfIncident: string;
  createdAt: string;
  nameOfPerson: string;
  commandUnit: string;
  currentAssignee: string;
  isPendingTransfer?: boolean;
  primaryButton: ButtonAction;
  secondaryButton?: ButtonAction;
};

function DesktopIncidentWorkItemCard(props: DesktopIncidentWorkItemCardProps) {
  return (
    <>
      {props.isPendingTransfer && <PendingTransferBox />}
      <Box
        sx={{
          display: "flex",
          gap: "2rem",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <HeadingAndSubheading
          heading={tk("incidentWorkItem.typeOfIncident")}
          subheading={props.typeOfIncident}
        />
        <HeadingAndSubheading
          heading={tk("incidentWorkItem.createdAt")}
          subheading={props.createdAt}
        />
        <HeadingAndSubheading
          heading={tk("incidentWorkItem.nameOfPerson")}
          subheading={props.nameOfPerson}
        />
        <HeadingAndSubheading
          heading={tk("incidentWorkItem.commandUnit")}
          subheading={props.commandUnit}
        />
        <HeadingAndSubheading
          heading={tk("incidentWorkItem.currentAssignee")}
          subheading={props.currentAssignee}
        />
        <Box sx={{ display: "flex", gap: "1rem" }}>
          {props.secondaryButton && (
            <SubmissionButton
              variant="outlined"
              onClick={async () => {
                await props.secondaryButton?.onClick();
              }}
              sx={{ minWidth: "5rem" }}
              data-testid="cancel"
            >
              <Translation
                {...buildTranslationInput(props.secondaryButton.label)}
              />
            </SubmissionButton>
          )}
          <Button
            variant="contained"
            onClick={() => {
              void props.primaryButton.onClick();
            }}
            sx={{ minWidth: "5rem" }}
            data-testid="viewIncident"
            disabled={props.primaryButton.disabled}
          >
            <Translation
              {...buildTranslationInput(props.primaryButton.label)}
            />
          </Button>
        </Box>
      </Box>
    </>
  );
}

type MobileIncidenWorkItemCardProps = DesktopIncidentWorkItemCardProps & {
  showAssignedOfficer: boolean;
};

function MobileIncidentWorkItemCard(props: MobileIncidenWorkItemCardProps) {
  const theme = useTheme();

  return (
    <>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          border: "1px solid",
          borderRadius: "0.5rem",
          borderColor: props.isPendingTransfer
            ? theme.palette.warning.main
            : theme.palette.grey[400],
          minHeight: "12rem",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {props.isPendingTransfer && (
          <Box
            sx={{
              backgroundColor: theme.palette.warning.main,
              px: 2,
              py: 0.75,
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: theme.palette.text.primary,
                textAlign: "center",
              }}
            >
              <Translation
                {...buildTranslationInput(
                  tk("incidentWorkItem.pendingTransfer"),
                )}
              />
            </Typography>
          </Box>
        )}
        <Stack
          sx={{
            padding: "1rem",
            gap: "1rem",
            justifyContent: "space-between",
            flexGrow: 1,
          }}
        >
          <Stack
            sx={{
              gap: "1rem",
              flexDirection: "column",
              display: "flex",
            }}
          >
            <Typography sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
              {props.typeOfIncident}
            </Typography>
            <HeadingAndSubheading
              heading={tk("incidentWorkItem.createdAt")}
              subheading={props.createdAt}
            />
            <HeadingAndSubheading
              heading={tk("incidentWorkItem.nameOfPerson")}
              subheading={props.nameOfPerson}
            />

            {props.showAssignedOfficer && (
              <HeadingAndSubheading
                heading={tk("incidentWorkItem.currentAssignee")}
                subheading={props.currentAssignee}
              />
            )}
          </Stack>

          <Stack
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              gap: "1rem",
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                void props.primaryButton.onClick();
              }}
              data-testid="viewIncident"
              disabled={props.primaryButton.disabled}
            >
              <Translation
                {...buildTranslationInput(props.primaryButton.label)}
              />
            </Button>
            {props.secondaryButton && (
              <SubmissionButton
                variant="outlined"
                onClick={async () => {
                  await props.secondaryButton?.onClick();
                }}
                data-testid="cancel"
              >
                <Translation
                  {...buildTranslationInput(props.secondaryButton.label)}
                />
              </SubmissionButton>
            )}
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}

export function IncidentWorkItemCard(props: IncidentWorkItemCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const {
    typeOfIncident,
    createdAt,
    nameOfPerson,
    commandUnit,
    currentAssignee,
  } = extractIncidentDisplayValues(props.incidentWorkItem, t);

  return isDesktop ? (
    <DesktopIncidentWorkItemCard
      primaryButton={props.primaryButton}
      secondaryButton={props.secondaryButton}
      isPendingTransfer={props.isPendingTransfer}
      typeOfIncident={typeOfIncident}
      createdAt={createdAt}
      nameOfPerson={nameOfPerson}
      commandUnit={commandUnit}
      currentAssignee={currentAssignee}
    />
  ) : (
    <MobileIncidentWorkItemCard
      showAssignedOfficer={props.showAssignedOfficerOnMobile}
      primaryButton={props.primaryButton}
      secondaryButton={props.secondaryButton}
      isPendingTransfer={props.isPendingTransfer}
      typeOfIncident={typeOfIncident}
      createdAt={createdAt}
      nameOfPerson={nameOfPerson}
      commandUnit={commandUnit}
      currentAssignee={currentAssignee}
    />
  );
}
